import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { MaskLines } from "./motion";
import { Container } from "./blocks";

const HeroImage = ({ y }) => {
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 80, damping: 15 });
  const sry = useSpring(ry, { stiffness: 80, damping: 15 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 10);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 10);
  };
  const reset = () => { rx.set(0); ry.set(0); };
  return (
    <motion.div style={{ y }} className="relative mx-auto w-full max-w-[460px] [perspective:1200px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div style={{ rotateX: srx, rotateY: sry }} initial={{ clipPath: "inset(100% 0 0 0 round 999px 999px 24px 24px)" }}
        animate={{ clipPath: "inset(0% 0 0 0 round 999px 999px 24px 24px)" }} transition={{ duration: 1.4, delay: 0.5, ease: [0.76, 0, 0.24, 1] }}
        className="relative aspect-[4/5] overflow-hidden rounded-t-full rounded-b-3xl border border-[#D4AF37]/40 shadow-[0_40px_120px_-30px_rgba(243,198,73,0.35)]">
        <img src="/images/hero.jpg" alt="Silhouette of a person in a wheelchair raising one arm high as they roll toward a bright opening"
          className="h-full w-full scale-110 object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E]/80 via-transparent to-[#F3C649]/10" />
        <p className="absolute bottom-5 left-5 right-5 text-xs uppercase tracking-[0.2em] text-[#F9F8F6]">Real experience → Practical next steps</p>
      </motion.div>
      <div aria-hidden="true" className="absolute -inset-6 -z-10 rounded-t-full border border-[#D4AF37]/20" />
    </motion.div>
  );
};

export const HomeHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="spotlight grain relative overflow-hidden pb-20 pt-32 sm:pt-40 lg:min-h-[100svh]" data-testid="home-hero">
      <Container className="relative grid items-center gap-14 lg:grid-cols-12">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p className="label" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            A John E. Mason Program
          </motion.p>
          <h1 className="mt-6 font-display text-[3.7rem] font-semibold leading-[0.9] tracking-tight sm:text-8xl xl:text-[9rem]" data-testid="hero-heading">
            <MaskLines lines={["Journey.", <em key="e" className="gold-text not-italic sm:italic">Empower.</em>, "Motivate."]} delay={0.25} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }}
            className="mt-8 max-w-xl text-lg leading-relaxed text-[#E6E3DA] sm:text-xl" data-testid="hero-supporting">
            A documentary-based program that turns real-life experience into conversation, confidence, and practical next steps.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.8 }} className="mt-10 flex flex-wrap gap-4">
            <Link to="/program" className="btn-ghost" data-testid="hero-explore-btn">Explore the Program</Link>
            <Link to="/contact" className="btn-gold" data-testid="hero-partner-btn">Discuss a Partnership <ArrowUpRight size={18} aria-hidden="true" /></Link>
          </motion.div>
        </motion.div>
        <div className="lg:col-span-5"><HeroImage y={imgY} /></div>
      </Container>
      <div aria-hidden="true" className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#C8C5BB] lg:flex">
        <ArrowDown size={14} className="animate-bounce text-[#F3C649]" /> Scroll
      </div>
    </section>
  );
};
